import axios from 'axios';

// Use relative URL to work with any domain/protocol
const BASE_URL = '/api';

export interface Product {
  sku: string;
  current_price: number | string | null;
  current_active_type: string | null;
  last_date: string | null;
}

export interface DailySales {
  daily_quantity: number;
  daily_revenue: number;
  daily_sales_count: number;
}

export interface SalesAggregatedData {
  daily_quantity: number;
  daily_revenue: number;
  daily_sales_count: number;
  avg_items_per_invoice: number;
}

export interface PriceHistory {
  sku: string;
  date: string;
  practiced_price: number | string | null;
  zmsr_price: number | string | null;
  zpr1_price: number | string | null;
  zpm3_price: number | string | null;
  active_type: string;
  daily_sales: DailySales;
  ga4_data?: GA4FunnelData;
}

export interface RawPriceData {
  sku: string;
  date: string;
  practiced_price: number | string | null;
  zmsr_price: number | string | null;
  zpr1_price: number | string | null;
  zpm3_price: number | string | null;
  active_type: string;
  validfrom: string | null;
  validto: string | null;
  processed_at: string;
}

export interface PriceDemandAnalysis {
  sku: string;
  product_name: string | null;
  practiced_price: number | string | null;
  total_quantity_sold: number;
  days_with_sales: number;
  total_days_at_price: number;
  days_without_sales: number;
  percentage_days_with_sales: number;
  demand_per_sales_day: number;
  demand_per_day: number;
  total_revenue: number | string;
  average_real_price: number | string | null;
  first_sale_at_price: string | null;
  last_sale_at_price: string | null;
  first_price_date: string | null;
  last_price_date: string | null;
  performance_classification: string | null;
}

export interface SalesAggregatedDebugData {
  sku: string;
  sale_date: string;
  daily_quantity: number;
  daily_revenue: number;
  daily_sales_count: number;
  avg_items_per_invoice: number;
  calculated_avg_price: number;
}

export interface PriceTimelineDebugData {
  sku: string;
  date: string;
  practiced_price: number | null;
  zmsr_price: number | null;
  zpr1_price: number | null;
  zpm3_price: number | null;
  active_type: string | null;
}

export interface DebugData {
  sales_data: SalesAggregatedDebugData[];
  price_data: PriceTimelineDebugData[];
}

export interface ProductStockHistory {
  snapshot_timestamp: string;
  plant: string;
  stock_quantity: number;
}

export interface Stats {
  total_products: number;
  total_records: number;
  earliest_date: string | null;
  latest_date: string | null;
}

export interface RankingWeightsConfig {
  weights: {
    revenue: number;
    stock: number;
    category: number;
    brand: number;
    potential_sales: number;
    quantity: number;
    price: number;
  };
  penalty_v2: {
    ultrafast: { dpd_threshold: number; decay: number; min: number; max_overdue: number };
    fast: { dpd_threshold: number; decay: number; min: number; max_overdue: number };
    medium: { dpd_threshold: number; decay: number; min: number; max_overdue: number };
    slow: { decay: number; min: number; max_overdue: number };
  };
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Frequently Bought Together (FBT) interfaces
export interface BasketPair {
  sku_a: string;
  sku_b: string;
  name_a?: string | null;
  name_b?: string | null;
  online_a?: boolean;
  online_b?: boolean;
  co_count: number;
  support: number | null;
  confidence_a_to_b: number | null;
  confidence_b_to_a: number | null;
  lift: number | null;
  jaccard: number | null;
  support_count_a: number;
  support_count_b: number;
  total_baskets: number;
  calculation_timestamp: string | null;
}

export interface BasketAssociation extends BasketPair {
  related_sku: string;
  related_name?: string | null;
  related_online?: boolean;
  /** Stock actual no site (Saleor) */
  related_in_stock?: boolean;
  confidence: number | null;
  /** Nº de cestos em que o produto relacionado aparece (suporte próprio) */
  related_baskets?: number;
  /** Origem da relação: par SKU→SKU ou família do SKU → SKU */
  source?: BasketSource;
  family_key?: string;
  /** Nº de SKUs da família que foram comprados com o relacionado */
  n_source_skus?: number;
  family_baskets?: number;
}

export type BasketLevel = 'sku' | 'family' | 'auto';
export type BasketSource = 'sku' | 'family_fine' | 'family_coarse';

export interface BasketFamily {
  coarse: string;
  fine: string | null;
  category: string | null;
  brand: string | null;
  key_filter: string | null;
  key_value: string | null;
}

export interface BasketPairsResponse {
  results: BasketPair[];
  count: number;
  calculation_timestamp: string | null;
}

export interface BasketAssociationsResponse {
  results: BasketAssociation[];
  count: number;
  sku: string;
  sku_name?: string | null;
  sku_online?: boolean | null;
  sku_in_stock?: boolean | null;
  calculation_timestamp: string | null;
  level?: BasketLevel;
  family?: BasketFamily | null;
}

// Semantic similarity (produtos semelhantes por título) interfaces
export interface SimilarProduct {
  neighbor_sku: string;
  neighbor_name: string | null;
  neighbor_category: string | null;
  neighbor_brand: string | null;
  neighbor_price?: number | null;
  similarity: number | null;
  same_category: boolean | null;
  rank: number | null;
  /** Stock actual no site (Saleor) */
  neighbor_in_stock?: boolean;
}

export interface SimilarProductsResponse {
  results: SimilarProduct[];
  count: number;
  sku: string;
  sku_name?: string | null;
  sku_category?: string | null;
  sku_price?: number | null;
  sku_in_stock?: boolean | null;
  calculation_timestamp: string | null;
}

// ===== Replenishment — hit rate das lojas =====
export interface HitRateMetrics {
  loja: string;          // A000..F000 ou 'TOTAL'
  encomendas: number;
  hits: number;
  encomendas_1sku: number;
  hits_1sku: number;
  encomendas_multi: number;
  hits_multi: number;
  falhas_com_stock_t: number;
  hits_potenciais: number;
  net_total: number;
  net_hit: number;
  encomendas_excluidas: number;          // só T000 (fornecedor/recondicionados)
  hit_rate: number | null;               // %
  hit_rate_1sku: number | null;
  hit_rate_multi: number | null;
  hit_rate_valor: number | null;
  pct_falhas_com_stock_t: number | null;
  hit_rate_potencial: number | null;
  // Hit real (SAP): nenhuma linha do site passou por 'Aguardar Produto'
  encomendas_sap: number;
  hits_sap: number;
  encomendas_com_espera: number;
  espera_satisfeitas: number;
  pendentes: number;
  espera_horas_soma: number;
  espera_ate_24h: number;
  espera_ate_48h: number;
  espera_0_4h: number;
  espera_4_12h: number;
  espera_12_24h: number;
  espera_24_48h: number;
  espera_48_72h: number;
  espera_72h_mais: number;
  hit_rate_real: number | null;
  espera_media_horas: number | null;
  pct_espera_ate_24h: number | null;
  pct_espera_ate_48h: number | null;
  espera_mediana_horas?: number | null;   // calculada na API (não se agrega)
  // Destino final (estado actual no SAP)
  enc_entregues: number;
  enc_canceladas: number;
  enc_parciais: number;
  enc_nao_levantadas: number;
  enc_abertas: number;
  net_entregues: number;
  net_canceladas: number;
  net_parciais: number;
  net_nao_levantadas: number;
  net_abertas: number;
  encomendas_destino: number;
  pct_entregue: number | null;
  pct_cancelada: number | null;
  pct_parcial: number | null;
  pct_nao_levantada: number | null;
  pct_aberta: number | null;
  valor_perdido: number;                  // cancelada + não levantada + aberta
}

export type ReplenishmentDestino = 'entregue' | 'cancelada' | 'parcial' | 'pronta_nao_levantada' | 'aberta';

export interface DestinoPorEspera {
  grupo: 'LOJAS' | 'T000';
  teve_espera: boolean;
  destino: ReplenishmentDestino;
  n: number;
  net: number;
}

export interface HitRateDailyRow extends HitRateMetrics {
  date: string;          // YYYY-MM-DD (Lisboa)
}

export interface HitRateSummaryRow extends HitRateMetrics {
  name: string | null;
  concordancia?: number | null;   // % de encomendas em que estimado == real
}

export interface HitRateResponse {
  start: string;
  end: string;
  stores: string[];
  store_names: Record<string, string | null>;
  daily: HitRateDailyRow[];
  summary: HitRateSummaryRow[];
  destino_x_espera?: DestinoPorEspera[];
  sap_history_start?: string;   // hit real (SAP) só existe a partir desta data
  calculated_at: string | null;
}

export type ReplenishmentCausa =
  | 'ok' | 'catalogacao' | 'distribuicao' | 'quantidade' | 'ruptura'
  | 'ruptura_t' | 'stock_so_em_lojas' | 'excluida_fornecedor' | 'excluida_recondicionado'
  | 'acrescentada_loja' | 'cancelada';

export interface ReplenishmentOrder {
  doc: string;
  loja: string;
  external_ref: string | null;
  order_ts: string | null;            // DATETIME hora de Lisboa, sem fuso
  n_skus: number;
  order_net: number;
  order_hit: boolean | null;          // null = excluída
  order_hit_se_t: boolean | null;
  order_excluded: boolean;
  order_hit_sap: boolean | null;      // hit real (SAP)
  espera_horas: number | null;        // até Pronta, desde a encomenda
  pendente: boolean | null;
  estado_sap: string | null;          // linha do site menos avançada
  destino_final: ReplenishmentDestino;
  dias_desde_encomenda: number;
  linhas_falhadas: number;
  linhas_acrescentadas: number;       // criadas na loja no levantamento (não contam)
  causas: ReplenishmentCausa[];
}

export interface ReplenishmentOrdersResponse {
  count: number;
  limit: number;
  offset: number;
  results: ReplenishmentOrder[];
}

export interface ReplenishmentOrderLine {
  sku: string;
  nome: string | null;
  cat1: string | null;
  cat2: string | null;
  estado: string | null;
  qty: number;
  net: number;
  stock_loja: number;
  stock_t: number;
  lojas_com_stock: number;
  stock_lojas_total: number;
  line_hit: boolean | null;           // null = acrescentada na loja
  added_in_store: boolean | null;
  causa: ReplenishmentCausa;
  snapshot_ts: string | null;
  teve_prod_sap: boolean | null;
  pronta_ts_sap: string | null;
  estado_actual_sap: string | null;
  sap_first_seen_ts: string | null;
  sap_prod_ts: string | null;
  sap_leva_ts: string | null;
  sap_env_ts: string | null;
  destino_linha: ReplenishmentDestino | null;
  destino_final: ReplenishmentDestino | null;
  motivo_rejeicao: string | null;
  line_cancelada: boolean | null;
  status: string | null;
  status_name: string | null;
}

export interface ReplenishmentOrderDetail {
  order: {
    doc: string;
    external_ref: string | null;
    loja: string;
    order_date: string;
    order_ts: string | null;
    n_skus: number;
    order_net: number;
    order_hit: boolean | null;
    order_hit_se_t: boolean | null;
    order_excluded: boolean;
    order_hit_sap: boolean | null;
    destino_final: ReplenishmentDestino | null;
    order_pronta_ts_sap: string | null;
    espera_horas: number | null;
    pendente: boolean | null;
    calculated_at: string | null;
  };
  lines: ReplenishmentOrderLine[];
}

export interface ReplenishmentSkuFailure {
  sku: string;
  nome: string | null;
  cat1: string | null;
  cat2: string | null;
  estado: string | null;
  linhas: number;
  falhas: number;
  qty_falhada: number;
  causa_dominante: ReplenishmentCausa | null;
}

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enviar cookies de sessão/CSRF (autenticação por sessão)
  withCredentials: true,
});

// Lê um cookie pelo nome (para o token CSRF do Django)
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Injecta X-CSRFToken nos métodos não-seguros (POST/PUT/PATCH/DELETE).
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method)) {
    const token = getCookie('csrftoken');
    if (token) {
      config.headers = config.headers || {};
      (config.headers as Record<string, string>)['X-CSRFToken'] = token;
    }
  }
  return config;
});

// Em 401 (sessão expirada / não autenticado) redirecciona para o login.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const path = window.location.pathname;
    if (status === 401 && path !== '/login') {
      window.location.assign('/login');
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  // Health check
  healthCheck: async () => {
    const response = await api.get('/health/');
    return response.data;
  },

  // System stats
  getStats: async (): Promise<Stats> => {
    const response = await api.get('/stats/');
    return response.data;
  },

  // Ranking weights config
  getRankingWeights: async (): Promise<RankingWeightsConfig> => {
    const response = await api.get('/config/ranking-weights/');
    return response.data;
  },

  // Products
  getProducts: async (params?: {
    search?: string;
    limit?: number;
    page?: number;
  }): Promise<PaginatedResponse<Product>> => {
    const response = await api.get('/products/', { params });
    return response.data;
  },

  getProduct: async (sku: string): Promise<Product> => {
    const response = await api.get(`/products/${sku}/`);
    return response.data;
  },

  // Price history
  getPriceHistory: async (sku: string, params?: {
    limit?: number;
  }): Promise<PriceHistory[]> => {
    const response = await api.get(`/products/${sku}/history/`, { params });
    return response.data;
  },

  getPriceHistoryRange: async (sku: string, params: {
    start_date: string;
    end_date: string;
  }): Promise<PriceHistory[]> => {
    const response = await api.get(`/products/${sku}/history/range/`, { params });
    return response.data;
  },

  // Raw price data
  getRawPriceData: async (sku: string, params?: {
    limit?: number;
  }): Promise<RawPriceData[]> => {
    const response = await api.get(`/products/${sku}/raw/`, { params });
    return response.data;
  },

  // Price demand analysis
  getPriceDemandAnalysis: async (sku: string): Promise<PriceDemandAnalysis[]> => {
    const response = await api.get(`/products/${sku}/demand/`);
    return response.data;
  },

  // Debug data for specific price
  getDebugDataForPrice: async (sku: string, price: number): Promise<DebugData> => {
    const response = await api.get(`/products/${sku}/debug/${price}/`);
    return response.data;
  },

  // Performance comparison
  getPerformanceComparison: async (request: PerformanceRequest): Promise<PerformanceComparison[]> => {
    const response = await api.post('/performance-comparison/', request);
    return response.data;
  },

  // Multi-timeframe comparison
  getMultiTimeframeComparison: async (skus: string[]): Promise<MultiTimeframeComparison[]> => {
    const response = await api.post('/multi-timeframe-comparison/', { skus }, {
      timeout: 120000 // 2 minutes timeout for complex multi-timeframe queries
    });
    return response.data;
  },

  // Sales aggregated data
  getSalesAggregated: async (sku: string, params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<{[date: string]: SalesAggregatedData}> => {
    const response = await api.get(`/products/${sku}/sales-aggregated/`, { params });
    return response.data;
  },

  // Sparkline data for charts
  getSparklineData: async (skus: string[]): Promise<SparklineData> => {
    const response = await api.post('/sparkline-data/', { skus });
    return response.data;
  },

  // Sales data for snapshot
  getSalesForSnapshot: async (sku: string, snapshotTimestamp: string, daysBack: number = 7): Promise<any> => {
    const response = await api.get(`/products/${sku}/sales-for-snapshot/`, {
      params: {
        snapshot_timestamp: snapshotTimestamp,
        days_back: daysBack
      }
    });
    return response.data;
  },

  // GA4 funnel analysis data
  getGA4FunnelData: async (sku: string, params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<{[date: string]: GA4FunnelData}> => {
    const response = await api.get(`/products/${sku}/ga4-funnel/`, { params });
    return response.data;
  },

  // Daily stock data
  getDailyStockData: async (sku: string, params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<{[date: string]: DailyStockData}> => {
    const response = await api.get(`/products/${sku}/daily-stock/`, { params });
    return response.data;
  },

  // Invoice averages data
  getInvoiceAverages: async (sku: string): Promise<InvoiceAverages> => {
    const response = await api.get(`/products/${sku}/invoice-averages/`);
    return response.data;
  },

  // Ranking methods
  getRankingSnapshots: async (params?: {
    limit?: number;
  }): Promise<RankingSnapshotsResponse> => {
    const response = await api.get('/rankings/snapshots/', { params });
    return response.data;
  },

  getNearestSnapshot: async (target: string): Promise<{ nearest: string | null; target: string }> => {
    const response = await api.get('/rankings/snapshots/nearest/', { params: { target } });
    return response.data;
  },

  getRankingSnapshot: async (timestamp: string, params?: {
    limit?: number;
    offset?: number;
    search?: string;
    category?: string;
    brand?: string;
    order_by?: 'position_asc' | 'position_desc' | 'score_asc' | 'score_desc' | 'penalty_asc' | 'penalty_desc' | 'last_sale_asc' | 'last_sale_desc' | 'expected_hours_asc' | 'expected_hours_desc';
  }): Promise<RankingSnapshotResponse> => {
    const response = await api.get(`/rankings/snapshot/${timestamp}/`, { params });
    return response.data;
  },

  getRankingSnapshotStats: async (timestamp: string): Promise<RankingSnapshotStatsResponse> => {
    const response = await api.get(`/rankings/snapshot/${timestamp}/stats/`);
    return response.data;
  },

  compareRankingSnapshots: async (request: RankingComparisonRequest): Promise<RankingComparisonResponse> => {
    const response = await api.post('/rankings/compare/', request, {
      timeout: 60000 // 1 minute timeout for comparison queries
    });
    return response.data;
  },

  simulateRankingWeights: async (request: RankingSimulateRequest): Promise<RankingSimulateResponse> => {
    const response = await api.post('/rankings/simulate/', request, {
      timeout: 60000
    });
    return response.data;
  },

  getRankingHealth: async (timestamp?: string): Promise<RankingHealth> => {
    const url = timestamp ? `/rankings/health/${timestamp}/` : '/rankings/health/';
    const response = await api.get(url);
    return response.data;
  },

  // Sales Monitoring methods
  runSalesMonitoringAnalysis: async (params?: {
    recent_days?: number;
    baseline_days?: number;
  }): Promise<SalesMonitoringAnalyzeResponse> => {
    const response = await api.post('/monitoring/analyze/', params || {}, {
      timeout: 180000 // 3 min timeout
    });
    return response.data;
  },

  getMonitoringSnapshots: async (params?: {
    limit?: number;
  }): Promise<MonitoringSnapshotsResponse> => {
    const response = await api.get('/monitoring/snapshots/', { params });
    return response.data;
  },

  getMonitoringSnapshotDetail: async (snapshotId: string): Promise<SalesMonitoringAnalyzeResponse> => {
    const response = await api.get(`/monitoring/snapshot/${snapshotId}/`);
    return response.data;
  },

  // Auditoria methods
  getPipelineStatusFromLogs: async (): Promise<PipelineExecutionLog[]> => {
    // Add timestamp to prevent browser caching
    const response = await api.get('/audit/pipeline-status/', {
      params: { _t: Date.now() }
    });
    return response.data.pipeline_status;
  },

  getApiAccessStats: async (hoursBack: number = 24): Promise<ApiAccessStat[]> => {
    const response = await api.get('/audit/api-stats/', {
      params: { hours: hoursBack }
    });
    return response.data.api_stats;
  },

  // Product Ranking Timeline
  getProductRankingTimeline: async (sku: string, hours: number = 24): Promise<RankingTimelineResponse> => {
    const response = await api.get(`/products/${sku}/ranking-timeline/`, {
      params: { hours }
    });
    return response.data;
  },

  // Penalty V2 Debug
  getProductPenaltyDebug: async (sku: string): Promise<PenaltyV2DebugResponse> => {
    const response = await api.get(`/products/${sku}/penalty-debug/`);
    return response.data;
  },

  getProductStockHistory: async (sku: string, days: number = 7): Promise<ProductStockHistory[]> => {
    const response = await api.get(`/products/${sku}/stock-history/`, {
      params: { days, _t: Date.now() }
    });
    return response.data;
  },

  // Price Changes
  getPriceChanges: async (date: string, params?: {
    limit?: number;
    offset?: number;
    order_by?: 'mudanca_pct_desc' | 'mudanca_pct_asc' | 'position_asc' | 'position_desc' | 'price_change_desc' | 'price_change_asc';
    search?: string;
  }): Promise<PriceChangesResponse> => {
    const response = await api.get(`/price-changes/${date}/`, { params });
    return response.data;
  },

  // Stock Forecast
  getStockForecast: async (params?: {
    limit?: number;
    offset?: number;
    order_by?: string;
    search?: string;
    include_zero_stock?: boolean;
  }): Promise<StockForecastResponse> => {
    const response = await api.get('/stock-forecast/', { params, timeout: 120000 });
    return response.data;
  },

  // Frequently Bought Together — pares globais
  getBasketPairs: async (params?: {
    min_lift?: number;
    min_support?: number;
    min_co_count?: number;
    order_by?: 'lift' | 'support' | 'confidence' | 'co_count';
    limit?: number;
  }): Promise<BasketPairsResponse> => {
    const response = await api.get('/basket-analysis/pairs/', { params, timeout: 60000 });
    return response.data;
  },

  // Frequently Bought Together — associações por SKU
  getBasketAssociationsForSku: async (
    sku: string,
    params?: {
      limit?: number;
      order_by?: 'lift' | 'support' | 'confidence' | 'co_count';
      min_related_baskets?: number;
      in_stock_only?: boolean;
      level?: BasketLevel;
    }
  ): Promise<BasketAssociationsResponse> => {
    const response = await api.get(`/basket-analysis/by-sku/${encodeURIComponent(sku)}/`, {
      params,
      timeout: 60000,
    });
    return response.data;
  },

  // Semantic similarity — produtos semelhantes por título
  getSimilarProducts: async (
    sku: string,
    params?: {
      same_category?: boolean;
      exclude_same_brand?: boolean;
      max_per_brand?: number;
      price_up_pct?: number;
      price_down_pct?: number;
      limit?: number;
      in_stock_only?: boolean;
    }
  ): Promise<SimilarProductsResponse> => {
    const response = await api.get(`/semantic-search/by-sku/${encodeURIComponent(sku)}/`, {
      params,
      timeout: 60000,
    });
    return response.data;
  },

  // Replenishment — hit rate das lojas (tabela pré-calculada)
  getStoreHitRate: async (params: {
    start: string;
    end: string;
    stores?: string;   // 'A000,B000'; omitido = todas
  }): Promise<HitRateResponse> => {
    const response = await api.get('/replenishment/hit-rate/', { params, timeout: 60000 });
    return response.data;
  },

  getReplenishmentOrders: async (params: {
    start: string;
    end: string;
    stores?: string;
    result?: string;
    causa?: string;
    search?: string;
    pending_days?: number;
    destino?: string;
    limit?: number;
    offset?: number;
    ordering?: string;
  }): Promise<ReplenishmentOrdersResponse> => {
    const response = await api.get('/replenishment/orders/', { params, timeout: 60000 });
    return response.data;
  },

  // CSV do drill down (filtros activos, sem paginação). Devolve o blob e o nome sugerido.
  exportReplenishmentOrders: async (params: {
    start: string;
    end: string;
    store?: string;
    stores?: string;
    result?: string;
    causa?: string;
    search?: string;
    pending_days?: number;
    destino?: string;
    ordering?: string;
    level: 'orders' | 'lines';
  }): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get('/replenishment/orders/export/', {
      params, responseType: 'blob', timeout: 180000,
    });
    const disposition: string = response.headers['content-disposition'] || '';
    const match = /filename="?([^";]+)"?/.exec(disposition);
    return { blob: response.data, filename: match ? match[1] : `hit-rate_${params.level}.csv` };
  },

  getReplenishmentOrderDetail: async (doc: string): Promise<ReplenishmentOrderDetail> => {
    const response = await api.get(`/replenishment/orders/${encodeURIComponent(doc)}/`, { timeout: 60000 });
    return response.data;
  },

  getReplenishmentSkus: async (params: {
    start: string;
    end: string;
    stores?: string;
    limit?: number;
  }): Promise<{ results: ReplenishmentSkuFailure[]; count: number }> => {
    const response = await api.get('/replenishment/skus/', { params, timeout: 60000 });
    return response.data;
  },
};

export interface PeriodData {
  quantity_sold: number;
  total_revenue: number;
  sales_count: number;
  avg_sales_price: number;
  avg_practiced_price: number;
  period_start: string | null;
  period_end: string | null;
}

export interface ComparisonData {
  quantity_change: number;
  revenue_change: number;
  sales_change: number;
  sales_price_change: number;
  practiced_price_change: number;
}

export interface PerformanceComparison {
  sku: string;
  product_name: string | null;
  timeframe_days: number;
  current_period: PeriodData;
  previous_period: PeriodData;
  comparison: ComparisonData;
}

export interface PerformanceRequest {
  skus: string[];
  timeframe_days: number;
}

export interface TimeframeMetric {
  current: number;
  previous: number;
  change: number;
}

export interface TimeframeData {
  quantity: TimeframeMetric;
  revenue: TimeframeMetric;
  sales_count: TimeframeMetric;
  sales_price: TimeframeMetric;
  practiced_price: TimeframeMetric;
}

export interface MultiTimeframeComparison {
  sku: string;
  product_name: string | null;
  timeframes: {
    [key: string]: TimeframeData; // '7d', '14d', '21d', '28d', '56d'
  };
}

export interface SparklineDataPoint {
  date: string;
  quantity: number;
  moving_average: number;
}

export interface SparklineData {
  [sku: string]: SparklineDataPoint[];
}

export interface GA4FunnelData {
  daily_views: number;
  daily_add_to_cart: number;
  daily_add_to_cart_rate_pct: number;
}

export interface DailyStockData {
  daily_stock_total: number;
}

export interface InvoiceAverages {
  sku: string;
  media_30d: number;
  media_90d: number;
  media_365d: number;
  media_global: number;
  total_faturas: number;
  last_updated: string | null;
}

// Ranking interfaces

export interface RankingSnapshot {
  sku: string;
  product_name: string | null;
  ranking_position: number | null;
  rank_atual: number | null;       // Ranking com produtos COM stock primeiro
  rank_c_stock: number | null;     // Ranking puro por score (ignora stock)
  rank_s_stock: number | null;     // Simulação "sem stock"
  final_score: number | null;
  base_score: number | null;
  penalty_applied: number | null;
  notas: string | null;
  calculation_timestamp: string | null;
  processed_at: string | null;
  preco_atual: number | null;
  stock_quantity: number | null;
  revenue_total: number | null;
  quantity_total: number | null;
  dpd_0: number | null;
  dpd_1: number | null;
  dpd_3: number | null;
  dpd_7: number | null;
  last_sale_timestamp: string | null;
  penalty_v2_factor: number | null;
  penalty_v2_category: string | null;
  active_hours_overdue: number | null;
  categoria_codigo: string | null;
  categoria_nome: string | null;
  marca_codigo: string | null;
  marca_nome: string | null;
  preco_anterior: number | null;
  mudanca_preco_pct: number | null;
  impacto_previsto_dpd_pct: number | null;
  norm_revenue: number | null;
  norm_stock: number | null;
  norm_category: number | null;
  norm_brand: number | null;
  norm_potential: number | null;
  norm_quantity: number | null;
  norm_price: number | null;
  weighted_revenue: number | null;
  weighted_stock: number | null;
  weighted_category: number | null;
  weighted_brand: number | null;
  weighted_potential: number | null;
  weighted_quantity: number | null;
  weighted_price: number | null;
  vendas_6_dias: number | null;
  vendas_historicas: number | null;
  potential_sales: number | null;
  elasticity_score: number | null;
  category_revenue: number | null;
  brand_revenue: number | null;
  dpd_esperado_atual: number | null;
  dpd_esperado_anterior: number | null;
}

export interface RankingSnapshotStats {
  total_products: number;
  avg_score: number;
  avg_penalty: number;
  products_with_penalties: number;
  products_with_boosts: number;
  products_with_price_changes: number;
  avg_price_change_pct: number;
  total_revenue: number;
  total_stock: number;
  unique_categories: number;
  unique_brands: number;
}

export interface RankingSnapshotData {
  position: number | null;
  score: number | null;
  penalty: number | null;
  notas: string | null;
  price: number | null;
}

export interface RankingChanges {
  movement_type: 'up' | 'down' | 'stable' | 'new' | 'disappeared';
  position_change: number;
  score_change: number;
  penalty_change: number;
  price_change_pct: number | null;
}

export interface RankingComparison {
  sku: string;
  product_name?: string;
  category: string | null;
  brand: string | null;
  snapshot1: RankingSnapshotData;
  snapshot2: RankingSnapshotData;
  changes: RankingChanges;
}

export interface RankingWeightsInput {
  revenue: number;
  quantity: number;
  stock: number;
  category: number;
  brand: number;
  potential_sales: number;
  price: number;
}

export interface RankingSimulateRequest {
  snapshot_timestamp: string;
  weights: RankingWeightsInput;
  top_n?: number;
}

export interface SimulatedProduct {
  sku: string;
  product_name: string | null;
  original_position: number | null;
  original_score: number | null;
  new_score: number;
  new_position: number;
  delta_position: number | null;
  penalty_applied: number;
  categoria_nome: string | null;
  marca_nome: string | null;
  preco_atual: number | null;
  dpd_7: number | null;
  penalty_v2_factor: number | null;
  stock_quantity: number | null;
}

export interface RankingSimulateResponse {
  base_snapshot: string;
  weights_applied: RankingWeightsInput;
  total_products: number;
  top_n: number;
  products: SimulatedProduct[];
  error?: string;
}

export interface RankingComparisonRequest {
  timestamp1: string;
  timestamp2: string;
  limit?: number;
  sort_by?: 'position1' | 'position2' | 'position_change' | 'position_change_desc' | 'score_change' | 'score_change_desc' | 'penalty_change' | 'penalty_change_desc';
}

export interface RankingSnapshotsResponse {
  snapshots: string[];
  count: number;
}

export interface RankingSnapshotResponse {
  results: RankingSnapshot[];
  count: number;
  total: number;
  timestamp: string;
  filters: {
    search: string | null;
    category: string | null;
    brand: string | null;
    order_by?: string;
  };
  pagination: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface RankingSnapshotStatsResponse {
  stats: RankingSnapshotStats;
  timestamp: string;
}

export interface RankingComparisonResponse {
  results: RankingComparison[];
  count: number;
  comparison: {
    timestamp1: string;
    timestamp2: string;
    sort_by: string;
  };
}

// Ranking Health Check interfaces

export interface RankingHealthSnapshotInfo {
  timestamp: string;
  freshness_hours: number;
}

export interface RankingHealthDataQuality {
  total_products: number;
  products_online_sap: number;
  coverage_pct: number;
  products_with_sales_30d: number;
  products_with_price: number;
  products_no_price: number;
  products_no_stock: number;
  products_no_category: number;
}

export interface VelocityCategory {
  category: string;
  count: number;
  avg_score: number;
  avg_penalty: number;
  min_score: number;
  max_score: number;
}

export interface Alert {
  type: string;
  count: number;
  message: string;
}

export interface AlertsData {
  critical: Alert[];
  warnings: Alert[];
  info: Alert[];
}

export interface PenaltyAnalysis {
  heavily_penalized_count: number;
  avg_penalty: number;
  stddev_penalty: number;
  p25: number;
  p50: number;
  p75: number;
}

export interface StockRange {
  range: string;
  count: number;
}

export interface StockAnalysis {
  distribution: StockRange[];
}

export interface CategoryInfo {
  categoria: string;
  count: number;
  avg_score: number;
}

export interface BrandInfo {
  marca: string;
  count: number;
  avg_score: number;
}

export interface PipelineScriptStatus {
  last_update: string | null;
  hours_ago: number | null;
  file_path?: string;
}

export interface PipelineStatus {
  promoplan: PipelineScriptStatus;
  invoice_averages: PipelineScriptStatus;
  hourly_sales: PipelineScriptStatus;
}

export interface StockSnapshotDate {
  date: string;
}

export interface RankingHealth {
  snapshot_info: RankingHealthSnapshotInfo;
  data_quality: RankingHealthDataQuality;
  velocity_distribution: VelocityCategory[];
  alerts: AlertsData;
  penalty_analysis: PenaltyAnalysis;
  stock_analysis: StockAnalysis;
  top_categories: CategoryInfo[];
  top_brands: BrandInfo[];
  pipeline_status: PipelineStatus;
  pipeline_audit?: PipelineExecutionLog[];  // Dados de auditoria do pipeline
  sales_sync?: SalesSyncInfo;  // Informações de sincronização de vendas
  stock_snapshots_dates?: StockSnapshotDate[];  // Últimas 5 datas de snapshots de stock (tabela raw)
}

// Sales Sync interface
export interface SalesSyncInfo {
  last_invoice_today: string | null;
  revenue_today: number;
  revenue_yesterday: number;
  avg_revenue_7d: number;
}

// Auditoria interfaces

export interface PipelineExecutionLog {
  pipeline_step: string;
  last_execution: string;
  hours_ago: number;
  status: string;
  duration_seconds: number | null;
  records_processed: number | null;
  error_message: string | null;
}

export interface ApiAccessStat {
  endpoint: string;
  total_requests: number;
  avg_response_time_ms: number;
  successful_requests: number;
  failed_requests: number;
  success_rate_pct: number;
  last_access: string | null;
  minutes_ago: number | null;
  max_records: number | null;
  avg_records: number | null;
}

// Sales Monitoring interfaces

export interface SalesMonitoringAlert {
  sku: string;
  product_name: string | null;
  alert_type: 'dramatic_increase' | 'high_avg';
  avg_units_per_invoice_recent: number;
  avg_units_per_invoice_baseline: number;
  avg_change_pct: number;
  total_invoices_recent: number;
  total_invoices_baseline: number;
  total_units_recent: number;
  total_units_baseline: number;
  categoria_nome: string | null;
  marca_nome: string | null;
  preco_atual: number | null;
}

export interface SalesMonitoringSnapshot {
  snapshot_id: string;
  analysis_timestamp: string;
  recent_period_days: number;
  baseline_period_days: number;
  total_alerts: number;
  dramatic_increase_count: number;
  high_avg_count: number;
  alerts?: SalesMonitoringAlert[];
}

export interface SalesMonitoringAnalyzeResponse {
  snapshot_id: string;
  analysis_timestamp: string;
  recent_period_days: number;
  baseline_period_days: number;
  total_alerts: number;
  dramatic_increase_count: number;
  high_avg_count: number;
  alerts: SalesMonitoringAlert[];
}

export interface MonitoringSnapshotsResponse {
  snapshots: SalesMonitoringSnapshot[];
  count: number;
}

// Product Ranking Timeline interfaces

export interface RankingTimelineSnapshot {
  timestamp: string | null;
  ranking_position: number | null;
  rank_atual: number | null;
  rank_c_stock: number | null;
  rank_s_stock: number | null;
  final_score: number | null;
  base_score: number | null;
  penalty_v2_factor: number | null;
  penalty_v2_category: string | null;
  active_hours_overdue: number | null;
  last_sale_timestamp: string | null;
  dpd_0: number | null;
  dpd_1: number | null;
  dpd_3: number | null;
  dpd_7: number | null;
  stock_quantity: number | null;
  preco_atual: number | null;
  notas: string | null;
}

export interface RankingTimelineResponse {
  sku: string;
  range_hours: number;
  snapshots: RankingTimelineSnapshot[];
  count: number;
}

// Penalty V2 Debug interfaces

export interface PenaltyV2ProductInfo {
  sku: string;
  product_name: string | null;
  calculation_timestamp: string | null;
  last_sale_timestamp: string | null;
}

export interface PenaltyV2DebugInfo {
  category: string;
  dpd_7: number;
  active_hours_overdue: number;
  decay_rate: number;
  min_penalty: number;
  expected_hours_between_sales: number;
  active_hours_since_sale: number;
  hours_since_last_sale: number;
  penalty_raw: number;
  penalty_final: number;
  test_mode: boolean;
  test_factor: number;
  discount_pct: number;
}

export interface PenaltyV2Config {
  decay_rate: number;
  min_penalty: number;
  dpd_threshold_for_next: number | null;
}

export interface PenaltyV2Calculation {
  penalty_factor: number;
  penalty_note: string;
  debug_info: PenaltyV2DebugInfo;
  config_used: PenaltyV2Config;
}

export interface PenaltyV2StoredValues {
  penalty_v2_factor: number | null;
  penalty_v2_category: string | null;
  active_hours_overdue: number | null;
}

export interface PenaltyV2Comparison {
  calculation_matches_stored: boolean;
  category_matches_stored: boolean;
  overdue_matches_stored: boolean;
}

export interface PenaltyV2DebugResponse {
  product_info: PenaltyV2ProductInfo;
  penalty_calculation: PenaltyV2Calculation;
  stored_values: PenaltyV2StoredValues;
  comparison: PenaltyV2Comparison;
}

// Price Changes interfaces

export interface PriceChangeItem {
  sku: string;
  product_name: string | null;
  ranking_position: number | null;
  final_score: number | null;
  base_score: number | null;
  preco_atual: number | null;
  preco_anterior: number | null;
  mudanca_preco_pct: number | null;
  impacto_previsto_dpd_pct: number | null;
  dpd_0: number | null;
  dpd_1: number | null;
  dpd_3: number | null;
  dpd_7: number | null;
  dpd_14: number | null;
  dpd_28: number | null;
  stock_quantity: number;
  penalty_applied: number | null;
  penalty_v2_factor: number | null;
  penalty_v2_category: string | null;
  categoria_nome: string | null;
  marca_nome: string | null;
  notas: string | null;
}

export interface PriceChangesResponse {
  results: PriceChangeItem[];
  total_count: number;
  increases_count: number;
  decreases_count: number;
  snapshot_timestamp: string | null;
  date: string;
}

export interface StockForecastItem {
  sku: string;
  product_name: string | null;
  ranking_position: number | null;
  stock_quantity: number;
  preco_atual: number | null;
  dpd_0: number | null;
  dpd_1: number | null;
  dpd_3: number | null;
  dpd_7: number | null;
  dpd_14: number | null;
  dpd_28: number | null;
  revenue_7d: number | null;
  revenue_28d: number | null;
  revenue_pct_7d: number | null;
  revenue_pct_28d: number | null;
  sales_potential: number | null;
  stockout_days_1d: number | null;
  stockout_days_3d: number | null;
  stockout_days_7d: number | null;
  stockout_days_14d: number | null;
  stockout_days_28d: number | null;
  categoria_nome: string | null;
  marca_nome: string | null;
  last_sale_timestamp: string | null;
}

export interface StockForecastResponse {
  results: StockForecastItem[];
  total_count: number;
  snapshot_timestamp: string | null;
}

// ===== Autenticação (sessão) =====
export interface AuthUser {
  username: string;
  email: string;
  is_staff: boolean;
  is_superuser: boolean;
}

export const authService = {
  // Garante o cookie CSRF antes de um POST de login
  ensureCsrf: async (): Promise<void> => {
    await api.get('/auth/csrf/');
  },
  login: async (username: string, password: string): Promise<AuthUser> => {
    await api.get('/auth/csrf/');
    const response = await api.post('/auth/login/', { username, password });
    return response.data;
  },
  logout: async (): Promise<void> => {
    await api.post('/auth/logout/');
  },
  // Devolve o utilizador autenticado, ou null se não houver sessão (401).
  me: async (): Promise<AuthUser | null> => {
    try {
      const response = await api.get('/auth/me/');
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return null;
      }
      throw error;
    }
  },
};

// ===== Gestão de API keys (staff) =====
export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  status: 'active' | 'revoked' | 'expired';
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_by: string | null;
  key?: string; // só presente na criação
}

export interface ApiScope {
  id: string;
  label: string;
}

export const apiKeysService = {
  list: async (): Promise<{ results: ApiKey[]; count: number }> => {
    const response = await api.get('/admin/api-keys/');
    return response.data;
  },
  getScopes: async (): Promise<ApiScope[]> => {
    const response = await api.get('/admin/scopes/');
    return response.data.scopes;
  },
  create: async (name: string, scopes: string[], expiresAt?: string | null): Promise<ApiKey> => {
    const response = await api.post('/admin/api-keys/', {
      name,
      scopes,
      expires_at: expiresAt || null,
    });
    return response.data;
  },
  revoke: async (id: string): Promise<ApiKey> => {
    const response = await api.post(`/admin/api-keys/${id}/revoke/`);
    return response.data;
  },
};

export default apiService;